import React, { useEffect, useState } from 'react';
import { useSelector, useDebounce, useLogger } from '~Hooks';
import styled from '@emotion/styled';
import { Microdeb } from '@baemingo/microdeb';

import NumberInput from '~ComponentsImproved/Numpad';
import { useI18n } from '~Hooks';
import { SwipeView } from '../GiftCard/Components/SwipeView';
import { ManualView } from '../GiftCard/Components/ManualView';
import { QRScanView } from '../GiftCard/Components/QRScanView';
import { handyBreakpoint } from '~Styles/breakpoints';

export const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 49;
  animation: fade-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

export const InnerWrapper = styled.div<{
  top?: number;
}>`
  position: absolute;
  top: 200px;
  width: 100%;
  text-align: center;
  font-size: 32px;
  color: #fff;
  font-weight: bold;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);

  ${handyBreakpoint} {
    font-size: 24px;
  }
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 200px;
  width: 100%;

  ${handyBreakpoint} {
    padding: 16px;
    margin-top: 64px;
    box-sizing: border-box;
  }
`;

export const ExitWrapper = styled.div<{
  top?: number;
}>`
  position: absolute;
  top: 200px;
  width: 100%;
  text-align: center;
  font-size: 35px;
  color: #fff;
  font-weight: bold;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

export const Subtitle = styled.div`
  font-size: 25px;
  color: #fff;
  font-weight: 300;
`;

export const ConfirmButton = styled.div`
  padding: 10px 15px;
  margin: 20px auto;
  color: #333;
  background: #fafafa;
  border-radius: 2px;
  width: 300px;
`;

export const Tabs = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  background: #a7a7a7;
  border-radius: 40px;
  height: 64px;
  width: 480px;

  ${handyBreakpoint} {
    width: 100%;
  }
`;

export const Tab = styled.div<{
  active?: boolean;
}>`
  width: 100%;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.active ? '#000' : '#fff')};
  background-color: ${(props) => (props.active ? '#fff' : '')};
  padding: 8px 12px;
  margin: 8px;
  border-radius: 40px;
  text-align: center;
  transition: all 0.1s ease-in;
  line-height: 1.8;

  ${handyBreakpoint} {
    font-size: 14px;
    line-height: 1.2;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

enum TAB_TYPES {
  SWIPE = 'SWIPE',
  MANUAL = 'MANUAL',
  QR = 'QR',
}

interface Props {
  id?: string;
  vat?: number;
  type: 'PAYMENT' | 'DEPOSIT' | 'REFUND';
  amount: number;
  isFailing: boolean;
  onComplete: (transactionId?: string) => void;
  onFail: (reason?: string) => void;
}

// test: 8fe9d622-f771-41a3-a6e1-09323ef5f0b6

// il tempo: 0685250f-b299-4755-94ea-ee47ca14aadd

const DEFAULT_ID = '0685250f-b299-4755-94ea-ee47ca14aadd';

const IDS: {
  [key: string]: string;
} = {
  // Bondegatan
  ckpfplnzqhdi30957yjzsjfd3: 'a78f3162-46e2-47f2-8f03-e0b87249fde0',

  // Pom flora odengatan
  ckpdvv712eohk0c57c7cq0py4: '34343b61-4041-4db3-8aeb-81207d2b331d',

  // Djursholms GK
  cko9rxvh3jar70957l2waa250: 'e9b86d56-286c-4b65-9170-d7e813267497',

  // Pom flora rörstrandsgatan
  cl8e7ip98pcb50897hqn7ynyn: '2cb2a0ca-943c-4eb9-b327-28bf95a79195',
};

const MicrodebPayment: React.FunctionComponent<Props> = ({
  id,
  vat,
  type,
  amount,
  isFailing,
  onComplete,
  onFail,
}) => {
  const [, , venueId] = location.hash.split('/');

  const staffId = useSelector((state) => state.staff?.id);
  const [amountToDeposit, setAmountToDeposit] = useState<number | null>(null);
  const [awaitingAmountInput, setAwaitingAmountInput] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [inputIsInvalid, setInputIsInvalid] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [availableBalance, setAvailableBalance] = useState<null | number>(null);
  const [cardNumber, setCardNumber] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { i18n } = useI18n();
  const logger = useLogger('refund-microdeb');

  const terminalId = IDS[venueId] ?? DEFAULT_ID;

  const fullCardNumber = useDebounce(cardNumber);

  function handleCompleted(transactionId?: string) {
    setIsCompleted(true);

    setTimeout(() => {
      onComplete(transactionId);
    }, 1000);
  }

  function onAcceptAndContinue(selectedAmount: number) {
    if (type === 'PAYMENT' && availableBalance! < selectedAmount) {
      setError('Not enough balance');
      return;
    }

    setLoading(true);

    const microdeb = new Microdeb({
      operatorId: staffId,
      cardId: fullCardNumber,
      terminalId,
    });

    if (type === 'PAYMENT') {
      microdeb
        .makePurchase({
          externalId: id,
          amount: selectedAmount,
          vat: Math.round(vat!),
        })
        .then((response) => {
          logger.info('Payment - Microdeb Payment successful.', {
            response,
          });
          handleCompleted(id);
        })
        .catch((err) => {
          console.error(err);
          setError(err.message);
          setLoading(false);
        });
    } else if (type === 'DEPOSIT') {
      logger.info('Deposit - Microdeb Deposit initiated.');
      microdeb
        .makeDeposit(selectedAmount)
        .then((response) => {
          logger.info('Deposit - Microdeb Deposit successful.');
          handleCompleted();
        })
        .catch((err) => {
          logger.error('Deposit - Microdeb Deposit failed.', {
            err,
          });
          console.error(err);
          setError(err.message);
          setLoading(false);
        });
    } else {
      logger.info('Refund - Microdeb Refund initiated.');
      microdeb
        .void({
          vat: Math.round(vat!),
          externalId: id,
          amount: amount,
        })
        .then((response) => {
          logger.info('Refund - Microdeb Refund successful.');
          handleCompleted();
        })
        .catch((err) => {
          logger.error('Refund - Microdeb Refund failed.', {
            err,
          });
          console.error(err);
          setError(err.message);
          setLoading(false);
        });
    }
  }

  useEffect(() => {
    if (!amountToDeposit) {
      return;
    }

    onAcceptAndContinue(amountToDeposit);
  }, [amountToDeposit]);

  /**
   * This will trigger when user has swiped the gift card
   */
  useEffect(() => {
    if (fullCardNumber.length <= 0) {
      return;
    }

    if (fullCardNumber.includes('E?') || fullCardNumber.length < 5) {
      setInputIsInvalid(true);
      return;
    }

    const microdeb = new Microdeb({
      cardId: fullCardNumber,
      terminalId,
    });

    microdeb
      .getHistory(0)
      .then((response) => {
        setAvailableBalance(response.Data.balance);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message);
      });
  }, [fullCardNumber]);

  const [tabType, setTabType] = useState(TAB_TYPES.SWIPE);

  return (
    <Wrapper
      className="background"
      onTouchStart={(e) => {
        if (
          !loading &&
          !isCompleted &&
          (e.target as HTMLDivElement).className.includes('background')
        ) {
          onFail('Background pressed');
        }
      }}>
      {(() => {
        if (isCompleted) {
          return (
            <ExitWrapper>
              {type === 'PAYMENT' ? 'Payment' : 'Deposit'} {i18n('GC.Completed')}
            </ExitWrapper>
          );
        }
        if (isFailing) {
          return <ExitWrapper>{i18n('GC.Canceled')}</ExitWrapper>;
        }
        if (awaitingAmountInput) {
          return (
            <NumberInput
              decimal
              maxCount={5}
              onSubmit={(digits: string) => {
                const val = Math.round(parseFloat(digits) * 100);

                setAmountToDeposit(val);
                setAwaitingAmountInput(false);
              }}
              onClose={() => {
                setAwaitingAmountInput(false);
                onFail('Cancel');
              }}
            />
          );
        }
        if (inputIsInvalid) {
          return (
            <InnerWrapper>
              {i18n('GC.InvalidInput')}
              <Subtitle>{i18n('GC.TryAgain')}</Subtitle>
              <Subtitle>
                {i18n('GC.InputWas')} {fullCardNumber}
              </Subtitle>
              <ConfirmButton
                onClick={() => {
                  setCardNumber('');
                  setInputIsInvalid(false);
                }}>
                {i18n('GC.OK')}
              </ConfirmButton>
            </InnerWrapper>
          );
        }
        if (error) {
          return (
            <InnerWrapper>
              {i18n('GC.Wrong')}
              <Subtitle>{error}</Subtitle>
              <ConfirmButton onClick={() => onFail(error)}>{i18n('GC.OK')}</ConfirmButton>
            </InnerWrapper>
          );
        }

        if (cardNumber !== fullCardNumber || loading) {
          return (
            <InnerWrapper>
              {i18n('GC.Loading')}
              <Subtitle>{i18n('GC.Wait')}</Subtitle>
            </InnerWrapper>
          );
        }

        if (availableBalance !== null) {
          return (
            <InnerWrapper>
              {i18n('GC.CardObtained')}
              <Subtitle>
                {i18n('GC.AvailableBalance')}: {(availableBalance / 100).toFixed(2)}
              </Subtitle>
              <ConfirmButton
                onClick={(e) => {
                  e.preventDefault();
                  if (type === 'DEPOSIT') {
                    setAwaitingAmountInput(true);
                  } else {
                    onAcceptAndContinue(amount);
                  }
                }}>
                {i18n('GC.OK')}
              </ConfirmButton>
            </InnerWrapper>
          );
        }

        return (
          <InputWrapper>
            <Tabs>
              <Tab
                onClick={() => setTabType(TAB_TYPES.SWIPE)}
                active={tabType === TAB_TYPES.SWIPE}>
                {i18n('GC.SWIPE')}
              </Tab>
              <Tab
                onClick={() => setTabType(TAB_TYPES.MANUAL)}
                active={tabType === TAB_TYPES.MANUAL}>
                {i18n('GC.MANUAL')}
              </Tab>
              <Tab onClick={() => setTabType(TAB_TYPES.QR)} active={tabType === TAB_TYPES.QR}>
                {i18n('GC.QR')}
              </Tab>
            </Tabs>

            {tabType === TAB_TYPES.SWIPE && (
              <SwipeView cardNumber={cardNumber} setCardNumber={setCardNumber} />
            )}
            {tabType === TAB_TYPES.MANUAL && <ManualView submit={setCardNumber} />}
            {tabType === TAB_TYPES.QR && <QRScanView submit={setCardNumber} />}
          </InputWrapper>
        );
      })()}
    </Wrapper>
  );
};

export default MicrodebPayment;
